// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// The hand-rolled geometry behind the pollution rose and the two grid views, kept out of the
// rendering code so it can be tested by POSITION rather than by eye.
//
// Area-only tests pass on visually broken layouts — an implementation that stacks every cell at
// the same origin conserves total area perfectly and renders as garbage. So the tests that
// import this assert bounds, pairwise non-overlap, and the absence of NaN.

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PolarCell {
  /** Inclusive start bearing in degrees, may be negative for the sector straddling north. */
  from: number;
  to: number;
  inner: number;
  outer: number;
}

/** A point on a circle, with 0° at the top and bearings running clockwise like a compass. */
export function polar(cx: number, cy: number, radius: number, degrees: number): [number, number] {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return [cx + radius * Math.cos(radians), cy + radius * Math.sin(radians)];
}

/** An annular sector as an SVG path. */
export function wedgePath(
  cx: number,
  cy: number,
  inner: number,
  outer: number,
  from: number,
  to: number
): string {
  const [x1, y1] = polar(cx, cy, outer, from);
  const [x2, y2] = polar(cx, cy, outer, to);
  const [x3, y3] = polar(cx, cy, inner, to);
  const [x4, y4] = polar(cx, cy, inner, from);
  const large = to - from > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${outer} ${outer} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`;
}

/**
 * Every cell of a rose, in polar coordinates. Sectors tile the full circle exactly once and
 * rings tile the annulus exactly once, so no two cells may overlap and none may fall outside
 * [calmRadius, maxRadius].
 */
export function roseCells(sectors: number, rings: number, calmRadius: number, maxRadius: number): PolarCell[] {
  const cells: PolarCell[] = [];
  const step = 360 / sectors;
  for (let sector = 0; sector < sectors; sector++) {
    const from = sector * step - step / 2;
    for (let ring = 0; ring < rings; ring++) {
      cells.push({
        from,
        to: from + step,
        inner: calmRadius + ((maxRadius - calmRadius) * ring) / rings,
        outer: calmRadius + ((maxRadius - calmRadius) * (ring + 1)) / rings,
      });
    }
  }
  return cells;
}

/** Do two annular sectors overlap in both the angular and the radial dimension? */
export function polarOverlap(a: PolarCell, b: PolarCell): boolean {
  const radial = Math.min(a.outer, b.outer) - Math.max(a.inner, b.inner);
  if (radial <= 1e-9) return false;
  const normalise = (degrees: number) => ((degrees % 360) + 360) % 360;
  const spans = (cell: PolarCell) => {
    const from = normalise(cell.from);
    const to = from + (cell.to - cell.from);
    return to <= 360 ? [[from, to]] : [[from, 360], [0, to - 360]];
  };
  for (const [aFrom, aTo] of spans(a)) {
    for (const [bFrom, bTo] of spans(b)) {
      if (Math.min(aTo, bTo) - Math.max(aFrom, bFrom) > 1e-9) return true;
    }
  }
  return false;
}

/**
 * The rectangles behind the hour × month calendar and the station × hour matrix. Both views draw
 * a plain tiling, but "plain" is exactly the kind of layout that ships stacked at one origin.
 */
export function gridRects(
  columns: number,
  rows: number,
  cellW: number,
  cellH: number,
  offsetX = 0,
  offsetY = 0,
  gap = 1
): Rect[] {
  const cells: Rect[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      cells.push({
        x: offsetX + column * cellW,
        y: offsetY + row * cellH,
        w: Math.max(0, cellW - gap),
        h: Math.max(0, cellH - gap),
      });
    }
  }
  return cells;
}

export function rectOverlapArea(a: Rect, b: Rect): number {
  const ox = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const oy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return ox * oy;
}
